import { useTryCallApi } from 'common/hooks/use-try-call-api'
import { AssetLedger } from 'common/models'
import { AssetsSuggestionsService, PresenterSuggestAssetCategoryResponse } from 'common/open-api'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Suggestion } from '../types'
import { useGetAssetCategory } from './use-get-asset-category'

const MAX_SUGGESTIONS = 4

const byConfidenceDesc = (a: { confidence?: number }, b: { confidence?: number }) =>
  (b.confidence ?? 0) - (a.confidence ?? 0)

export type File = {
  id: number
  filename: string
  thumbnail_url: string
  url: string
}

export const useAISuggestion = () => {
  const { assetCategoryList } = useGetAssetCategory()

  const { setFieldValue, values } = useFormikContext()
  const [assetName, setAssetName] = useState<string | undefined>(undefined)
  const [image, setImage] = useState<File | undefined>(undefined)
  const { data, callApi, isLoading } = useTryCallApi(AssetsSuggestionsService.suggestionsCreate)
  const [responseData, setResponseData] = useState<PresenterSuggestAssetCategoryResponse | undefined>()
  const fixedAssetLedgers: AssetLedger[] = get(values, 'fixed_asset_ledgers')

  useEffect(() => {
    if (data) {
      setResponseData(data?.data)
    }
  }, [data])

  const onSubmit = useCallback(
    async (image?: File) => {
      setImage(image)

      const payload = image
        ? { file_id: image.id.toString(), ...(assetName && { asset_name: assetName }) }
        : { asset_name: assetName }
      await callApi(payload)
    },
    [assetName]
  )

  const onTryAgain = useCallback(() => {
    setResponseData(undefined)
    setAssetName(undefined)
    setImage(undefined)
  }, [])

  const onReflect = (suggestion?: Suggestion) => {
    if (!suggestion) return

    const assetCategoryInfo = assetCategoryList.find(item => item.code === suggestion?.asset_category_code)
    setFieldValue('asset_category_id', { value: assetCategoryInfo?.id, label: assetCategoryInfo?.name })

    for (let i = 0; i < fixedAssetLedgers?.length; i++) {
      setFieldValue(`fixed_asset_ledgers[${i}].service_life`, assetCategoryInfo?.service_life)
    }
  }

  const { suggestions, hasNoSuggestions } = useMemo((): {
    suggestions: Suggestion[]
    hasNoSuggestions: boolean
  } => {
    if (!responseData) return { suggestions: [], hasNoSuggestions: true }

    const {
      asset_category_code,
      asset_category_name,
      service_life,
      reasoning,
      confidence,
      alternative_matches,
      proposed_new_category,
    } = responseData

    const mainSuggestion: Suggestion = {
      asset_name: assetName,
      asset_category_code,
      asset_category_name,
      service_life,
      reasoning,
      confidence,
    }

    const proposedNewCategory = proposed_new_category
      ? {
          asset_category_name: proposed_new_category.asset_category_name,
          reasoning: proposed_new_category.reasoning,
          service_life: proposed_new_category.service_life,
          is_proposed_new_category: true,
        }
      : {}

    const withAssetName = (item: Suggestion) => ({ ...item, asset_name: assetName })
    const byAssetCategoryName = (item: Suggestion) => item.asset_category_name
    const candidates: Suggestion[] = alternative_matches
      ? [mainSuggestion, withAssetName(proposedNewCategory), ...alternative_matches.map(withAssetName)]
      : [mainSuggestion]

    const suggestions = [...candidates].filter(byAssetCategoryName).sort(byConfidenceDesc).slice(0, MAX_SUGGESTIONS)
    return {
      suggestions,
      hasNoSuggestions: candidates.length === 0,
    }
  }, [responseData, assetName])

  const isInitialState = responseData === undefined

  return {
    image,
    onSubmit,
    assetName,
    setAssetName,
    suggestions,
    hasNoSuggestions,
    isLoading,
    isInitialState,
    onTryAgain,
    onReflect,
  }
}
